import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import moment from "moment";
import styled from "styled-components";
import { CommonTableHeadCell } from "../../controls/components/commonTable/CommonTable";
import NumberPaginationBox from "../../controls/components/numberPaginationBox/NumberPaginationBox";
import {
	BookingContentName,
	BookingContentWrap,
	MyBookingTableContainerAdmin,
	MyBookingWrapper,
	PaginationWrapper,
	PaginationWrapperTop,
} from "../MyBooking/styled/DesStyled";
import {
	AdminSearchTrackingAnalytics,
	AdminSearchTrackingRecord,
	EMPTY_SEARCH_TRACKING_ANALYTICS,
	getAdminSearchTrackingEvents,
} from "../../../services/SearchTrackingService";

const PER_PAGE = 20;

const SOURCE_META: Record<string, { label: string; color: string; accent: string }> = {
	home_search: {
		label: "Homepage search",
		color: "#2563EB",
		accent: "#DBEAFE",
	},
	find_location_header_search: {
		label: "Map header search",
		color: "#0F766E",
		accent: "#CCFBF1",
	},
	find_location_map_search: {
		label: "Map sticky search",
		color: "#7C3AED",
		accent: "#EDE9FE",
	},
};

const MODE_META: Record<string, { label: string; color: string; accent: string }> = {
	pinned_search: {
		label: "Pinned 5km search",
		color: "#DC2626",
		accent: "#FEE2E2",
	},
	destination_search: {
		label: "Destination search",
		color: "#EA580C",
		accent: "#FFEDD5",
	},
	unknown: {
		label: "Unknown mode",
		color: "#6B7280",
		accent: "#F3F4F6",
	},
};

const EVENT_META: Record<string, { label: string; color: string; accent: string }> = {
	custom_search: {
		label: "Search",
		color: "#1D4ED8",
		accent: "#DBEAFE",
	},
	custom_near_me: {
		label: "Near me",
		color: "#0F766E",
		accent: "#CCFBF1",
	},
};

const STATUS_META: Record<string, { label: string; color: string; accent: string }> = {
	success: {
		label: "Success",
		color: "#166534",
		accent: "#DCFCE7",
	},
	no_result: {
		label: "No result",
		color: "#92400E",
		accent: "#FEF3C7",
	},
	error: {
		label: "Error",
		color: "#B91C1C",
		accent: "#FEE2E2",
	},
	permission_denied: {
		label: "Permission denied",
		color: "#B91C1C",
		accent: "#FEE2E2",
	},
	unsupported: {
		label: "Unsupported",
		color: "#6B7280",
		accent: "#F3F4F6",
	},
};

const DONUT_COLORS = ["#2563EB", "#0F766E", "#7C3AED", "#EA580C", "#DC2626", "#6B7280"];

const StatsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
	gap: 16px;
	margin-bottom: 24px;
`;

const StatCard = styled.div<{ $accent: string }>`
	background: #ffffff;
	border-radius: 18px;
	padding: 22px;
	box-shadow: 0px 4px 14.6px rgba(0, 0, 0, 0.08);
	border: 1px solid ${(props) => props.$accent};
`;

const StatLabel = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	margin-bottom: 10px;
`;

const StatValue = styled.div`
	font-size: 34px;
	font-weight: 700;
	line-height: 1;
	color: #111827;
	margin-bottom: 8px;
`;

const StatHint = styled.div`
	font-size: 14px;
	color: #4b5563;
	line-height: 1.5;
`;

const AnalyticsGrid = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 18px;
	margin-bottom: 24px;

	@media (max-width: 1200px) {
		grid-template-columns: 1fr;
	}
`;

const Panel = styled.div`
	background: #ffffff;
	border-radius: 18px;
	padding: 22px;
	box-shadow: 0px 4px 14.6px rgba(0, 0, 0, 0.08);
`;

const PanelHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 18px;
`;

const PanelTitle = styled.h3`
	margin: 0;
	font-size: 20px;
	font-weight: 700;
	color: #111827;
`;

const PanelHint = styled.p`
	margin: 6px 0 0;
	font-size: 14px;
	color: #6b7280;
	line-height: 1.5;
`;

const HighlightBadge = styled.div`
	padding: 8px 12px;
	border-radius: 999px;
	background: #f3f4f6;
	font-size: 12px;
	font-weight: 700;
	color: #374151;
	white-space: nowrap;
`;

const DonutLayout = styled.div`
	display: grid;
	grid-template-columns: 220px minmax(0, 1fr);
	gap: 20px;
	align-items: center;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`;

const DonutChartWrap = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
`;

const DonutChart = styled.div<{ $background: string }>`
	width: 220px;
	height: 220px;
	border-radius: 50%;
	background: ${(props) => props.$background};
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.55);
`;

const DonutHole = styled.div`
	width: 124px;
	height: 124px;
	border-radius: 50%;
	background: #ffffff;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	box-shadow: 0px 6px 20px rgba(15, 23, 42, 0.08);
	padding: 14px;
`;

const DonutCenterLabel = styled.div`
	font-size: 12px;
	font-weight: 700;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.08em;
`;

const DonutCenterValue = styled.div`
	font-size: 32px;
	font-weight: 800;
	color: #111827;
	line-height: 1.1;
	margin-top: 6px;
`;

const LegendList = styled.div`
	display: grid;
	gap: 12px;
`;

const LegendRow = styled.div`
	display: grid;
	grid-template-columns: auto minmax(0, 1fr) auto;
	gap: 12px;
	align-items: center;
	padding: 10px 12px;
	border: 1px solid #e5e7eb;
	border-radius: 14px;
`;

const LegendSwatch = styled.div<{ $color: string }>`
	width: 12px;
	height: 12px;
	border-radius: 999px;
	background: ${(props) => props.$color};
`;

const LegendLabel = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: #111827;
`;

const LegendValue = styled.div`
	font-size: 13px;
	font-weight: 700;
	color: #374151;
	white-space: nowrap;
`;

const RankedList = styled.div`
	display: grid;
	gap: 12px;
`;

const RankedItem = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	gap: 16px;
	align-items: center;
	padding: 12px 14px;
	border-radius: 14px;
	border: 1px solid #e5e7eb;
`;

const RankedLabel = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: #111827;
	word-break: break-word;
`;

const RankedValue = styled.div`
	font-size: 13px;
	font-weight: 700;
	color: #374151;
`;

const TrendList = styled.div`
	display: grid;
	gap: 12px;
`;

const TrendRow = styled.div`
	display: grid;
	grid-template-columns: 92px minmax(0, 1fr) auto;
	gap: 12px;
	align-items: center;
`;

const TrendLabel = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: #374151;
`;

const TrendStack = styled.div`
	display: flex;
	gap: 4px;
	height: 10px;
	border-radius: 999px;
	overflow: hidden;
	background: #f3f4f6;
`;

const TrendSegment = styled.div<{ $width: number; $color: string }>`
	width: ${(props) => Math.max(props.$width, 0)}%;
	background: ${(props) => props.$color};
`;

const TrendValue = styled.div`
	font-size: 13px;
	font-weight: 700;
	color: #111827;
`;

const RecentList = styled.div`
	display: grid;
	gap: 12px;
`;

const RecentCard = styled.div`
	border-radius: 14px;
	border: 1px solid #e5e7eb;
	padding: 14px 16px;
	display: grid;
	gap: 8px;
`;

const RecentTop = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 12px;
	align-items: center;
`;

const RecentTitle = styled.div`
	font-size: 15px;
	font-weight: 600;
	color: #111827;
`;

const RecentTimestamp = styled.div`
	font-size: 12px;
	color: #6b7280;
	white-space: nowrap;
`;

const RecentMeta = styled.div`
	font-size: 13px;
	color: #4b5563;
	line-height: 1.5;
	word-break: break-word;
`;

const DetailText = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 6px;
	line-height: 1.45;
`;

const MetaBadge = styled.span<{ $color: string; $accent: string }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 4px 10px;
	border-radius: 999px;
	background: ${(props) => props.$accent};
	color: ${(props) => props.$color};
	font-size: 12px;
	font-weight: 700;
`;

const EmptyState = styled.div`
	padding: 28px 0;
	text-align: center;
	color: #6b7280;
	font-size: 14px;
`;

function getSourceMeta(searchSource?: string | null) {
	return (
		SOURCE_META[searchSource || ""] || {
			label: searchSource || "Unknown source",
			color: "#6B7280",
			accent: "#F3F4F6",
		}
	);
}

function getModeMeta(searchMode?: string | null) {
	return (
		MODE_META[searchMode || ""] || {
			label: searchMode || "Unknown mode",
			color: "#6B7280",
			accent: "#F3F4F6",
		}
	);
}

function getEventMeta(eventType?: string | null) {
	return (
		EVENT_META[eventType || ""] || {
			label: eventType || "Unknown event",
			color: "#6B7280",
			accent: "#F3F4F6",
		}
	);
}

function getStatusMeta(status?: string | null) {
	return (
		STATUS_META[status || ""] || {
			label: status || "Unknown status",
			color: "#6B7280",
			accent: "#F3F4F6",
		}
	);
}

function buildDonutBackground(segments: Array<{ value: number; color: string }>) {
	const validSegments = segments.filter((segment) => segment.value > 0);
	const total = validSegments.reduce((sum, segment) => sum + segment.value, 0);

	if (!total) {
		return "conic-gradient(#E5E7EB 0deg 360deg)";
	}

	let currentAngle = 0;
	const stops = validSegments.map((segment) => {
		const start = currentAngle;
		currentAngle += (segment.value / total) * 360;
		return `${segment.color} ${start}deg ${currentAngle}deg`;
	});

	return `conic-gradient(${stops.join(", ")})`;
}

function formatSourceLabel(searchSource?: string | null) {
	return getSourceMeta(searchSource).label;
}

function formatModeLabel(searchMode?: string | null) {
	return getModeMeta(searchMode).label;
}

function formatStatusLabel(record: AdminSearchTrackingRecord) {
	if (record.eventType === "custom_near_me") {
		return getStatusMeta(record.nearMeStatus).label;
	}
	return getStatusMeta(record.searchStatus).label;
}

function formatCoordinates(record: AdminSearchTrackingRecord) {
	if (!record.searchLat || !record.searchLng) {
		return "--";
	}
	return `${record.searchLat}, ${record.searchLng}`;
}

export default function SearchTrackingPage() {
	const [docs, setDocs] = useState<AdminSearchTrackingRecord[]>([]);
	const [analytics, setAnalytics] = useState<AdminSearchTrackingAnalytics>(EMPTY_SEARCH_TRACKING_ANALYTICS);
	const [page, setPage] = useState(1);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		let mounted = true;

		const load = async () => {
			setLoading(true);
			try {
				const response = await getAdminSearchTrackingEvents({ page, perPage: PER_PAGE });
				if (!mounted) return;
				setDocs(response.docs);
				setAnalytics(response.analytics || EMPTY_SEARCH_TRACKING_ANALYTICS);
				setTotal(response.total);
			} catch (error) {
				if (!mounted) return;
				console.error(error);
				setDocs([]);
				setAnalytics(EMPTY_SEARCH_TRACKING_ANALYTICS);
				setTotal(0);
			} finally {
				if (mounted) {
					setLoading(false);
				}
			}
		};

		load();
		return () => {
			mounted = false;
		};
	}, [page]);

	const sourceSegments = useMemo(() => {
		return analytics.bySource.map((item, index) => ({
			key: item.searchSource,
			label: formatSourceLabel(item.searchSource),
			value: item.totalEvents,
			share: item.share,
			color: getSourceMeta(item.searchSource).color || DONUT_COLORS[index % DONUT_COLORS.length],
		}));
	}, [analytics.bySource]);

	const modeSegments = useMemo(() => {
		return analytics.byMode.map((item, index) => ({
			key: item.searchMode,
			label: formatModeLabel(item.searchMode),
			value: item.totalEvents,
			share: item.share,
			color: getModeMeta(item.searchMode).color || DONUT_COLORS[index % DONUT_COLORS.length],
		}));
	}, [analytics.byMode]);

	const maxTrendValue = Math.max(...analytics.dailyTrend.map((item) => item.totalEvents), 1);
	const leadingSource = sourceSegments[0];
	const leadingMode = modeSegments[0];

	return (
		<MyBookingWrapper>
			<BookingContentWrap>
				<BookingContentName>Landing search tracking</BookingContentName>

				<StatsGrid>
					<StatCard $accent="#E5E7EB">
						<StatLabel>Total tracked events</StatLabel>
						<StatValue>{analytics.totalEvents}</StatValue>
						<StatHint>All custom search and Near me events collected from the landing flow.</StatHint>
					</StatCard>
					<StatCard $accent="#E5E7EB">
						<StatLabel>Unique sessions</StatLabel>
						<StatValue>{analytics.uniqueSessions}</StatValue>
						<StatHint>Browser sessions that generated at least one tracked landing search signal.</StatHint>
					</StatCard>
					<StatCard $accent="#DCFCE7">
						<StatLabel>Successful searches</StatLabel>
						<StatValue>{analytics.successfulSearches}</StatValue>
						<StatHint>Searches that resolved to a concrete place and navigated into the map flow.</StatHint>
					</StatCard>
					<StatCard $accent="#FEF3C7">
						<StatLabel>No-result searches</StatLabel>
						<StatValue>{analytics.noResultSearches}</StatValue>
						<StatHint>Queries where FE search finished without a usable place result.</StatHint>
					</StatCard>
					<StatCard $accent="#DBEAFE">
						<StatLabel>Near me taps</StatLabel>
						<StatValue>{analytics.nearMeRequests}</StatValue>
						<StatHint>{analytics.nearMeSuccess} of them completed with a browser location result.</StatHint>
					</StatCard>
				</StatsGrid>

				<AnalyticsGrid>
					<Panel>
						<PanelHeader>
							<div>
								<PanelTitle>Source mix</PanelTitle>
								<PanelHint>Which search entry point on the FE is generating the most activity.</PanelHint>
							</div>
							<HighlightBadge>Leader: {leadingSource ? leadingSource.label : "No data"}</HighlightBadge>
						</PanelHeader>
						{sourceSegments.length > 0 ? (
							<DonutLayout>
								<DonutChartWrap>
									<DonutChart $background={buildDonutBackground(sourceSegments)}>
										<DonutHole>
											<DonutCenterLabel>Events</DonutCenterLabel>
											<DonutCenterValue>{analytics.totalEvents}</DonutCenterValue>
										</DonutHole>
									</DonutChart>
								</DonutChartWrap>
								<LegendList>
									{sourceSegments.map((item) => (
										<LegendRow key={item.key}>
											<LegendSwatch $color={item.color} />
											<LegendLabel>{item.label}</LegendLabel>
											<LegendValue>
												{item.value} . {item.share}%
											</LegendValue>
										</LegendRow>
									))}
								</LegendList>
							</DonutLayout>
						) : (
							<EmptyState>No search-source data yet.</EmptyState>
						)}
					</Panel>

					<Panel>
						<PanelHeader>
							<div>
								<PanelTitle>Search mode mix</PanelTitle>
								<PanelHint>How often users fall into pinned 5km search versus broad destination search.</PanelHint>
							</div>
							<HighlightBadge>Leader: {leadingMode ? leadingMode.label : "No data"}</HighlightBadge>
						</PanelHeader>
						{modeSegments.length > 0 ? (
							<DonutLayout>
								<DonutChartWrap>
									<DonutChart $background={buildDonutBackground(modeSegments)}>
										<DonutHole>
											<DonutCenterLabel>Searches</DonutCenterLabel>
											<DonutCenterValue>{analytics.totalSearches}</DonutCenterValue>
										</DonutHole>
									</DonutChart>
								</DonutChartWrap>
								<LegendList>
									{modeSegments.map((item) => (
										<LegendRow key={item.key}>
											<LegendSwatch $color={item.color} />
											<LegendLabel>{item.label}</LegendLabel>
											<LegendValue>
												{item.value} . {item.share}%
											</LegendValue>
										</LegendRow>
									))}
								</LegendList>
							</DonutLayout>
						) : (
							<EmptyState>No search-mode data yet.</EmptyState>
						)}
					</Panel>
				</AnalyticsGrid>

				<AnalyticsGrid>
					<Panel>
						<PanelHeader>
							<div>
								<PanelTitle>Top search terms</PanelTitle>
								<PanelHint>Raw queries users typed before search resolution or failure.</PanelHint>
							</div>
							<HighlightBadge>{analytics.topSearchTerms.length} terms shown</HighlightBadge>
						</PanelHeader>
						{analytics.topSearchTerms.length > 0 ? (
							<RankedList>
								{analytics.topSearchTerms.map((item) => (
									<RankedItem key={item.searchTerm}>
										<RankedLabel>{item.searchTerm}</RankedLabel>
										<RankedValue>{item.totalEvents}</RankedValue>
									</RankedItem>
								))}
							</RankedList>
						) : (
							<EmptyState>No search terms recorded yet.</EmptyState>
						)}
					</Panel>

					<Panel>
						<PanelHeader>
							<div>
								<PanelTitle>Top resolved results</PanelTitle>
								<PanelHint>Place labels that users successfully resolved to on the map flow.</PanelHint>
							</div>
							<HighlightBadge>{analytics.topResultLabels.length} places shown</HighlightBadge>
						</PanelHeader>
						{analytics.topResultLabels.length > 0 ? (
							<RankedList>
								{analytics.topResultLabels.map((item) => (
									<RankedItem key={item.searchResultLabel}>
										<RankedLabel>{item.searchResultLabel}</RankedLabel>
										<RankedValue>{item.totalEvents}</RankedValue>
									</RankedItem>
								))}
							</RankedList>
						) : (
							<EmptyState>No successful result labels yet.</EmptyState>
						)}
					</Panel>
				</AnalyticsGrid>

				<AnalyticsGrid>
					<Panel>
						<PanelHeader>
							<div>
								<PanelTitle>Demand timeline</PanelTitle>
								<PanelHint>Daily activity for the last 14 days, split by successful search, no-result, and Near me.</PanelHint>
							</div>
							<HighlightBadge>Window: 14 days</HighlightBadge>
						</PanelHeader>
						<TrendList>
							{analytics.dailyTrend.map((item) => {
								const totalWidth = (item.totalEvents / maxTrendValue) * 100;
								const successWidth = item.totalEvents
									? (item.successfulSearches / item.totalEvents) * totalWidth
									: 0;
								const noResultWidth = item.totalEvents
									? (item.noResultSearches / item.totalEvents) * totalWidth
									: 0;
								const nearMeWidth = item.totalEvents
									? (item.nearMeRequests / item.totalEvents) * totalWidth
									: 0;

								return (
									<TrendRow key={item.date}>
										<TrendLabel>{moment(item.date).format("DD MMM")}</TrendLabel>
										<TrendStack>
											<TrendSegment $width={successWidth} $color="#166534" />
											<TrendSegment $width={noResultWidth} $color="#D97706" />
											<TrendSegment $width={nearMeWidth} $color="#2563EB" />
										</TrendStack>
										<TrendValue>{item.totalEvents}</TrendValue>
									</TrendRow>
								);
							})}
							{analytics.dailyTrend.length === 0 && <EmptyState>No timeline data yet.</EmptyState>}
						</TrendList>
					</Panel>

					<Panel>
						<PanelHeader>
							<div>
								<PanelTitle>Recent signals</PanelTitle>
								<PanelHint>The latest FE search events received by backend tracking.</PanelHint>
							</div>
							<HighlightBadge>{analytics.recentEvents.length} latest records</HighlightBadge>
						</PanelHeader>
						<RecentList>
							{analytics.recentEvents.map((item) => {
								const eventMeta = getEventMeta(item.eventType);
								const statusMeta =
									item.eventType === "custom_near_me"
										? getStatusMeta(item.nearMeStatus)
										: getStatusMeta(item.searchStatus);
								return (
									<RecentCard key={item.id}>
										<RecentTop>
											<RecentTitle>{item.searchTerm || item.searchResultLabel || eventMeta.label}</RecentTitle>
											<RecentTimestamp>{moment(item.createdAt).format("HH:mm DD-MM-YYYY")}</RecentTimestamp>
										</RecentTop>
										<RecentMeta>
											Source: {formatSourceLabel(item.searchSource)} . Trigger: {item.searchTrigger || "--"}
										</RecentMeta>
										<RecentMeta>
											Result: {item.searchResultLabel || "--"} . Mode: {formatModeLabel(item.searchMode)}
										</RecentMeta>
										<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
											<MetaBadge $color={eventMeta.color} $accent={eventMeta.accent}>
												{eventMeta.label}
											</MetaBadge>
											<MetaBadge $color={statusMeta.color} $accent={statusMeta.accent}>
												{statusMeta.label}
											</MetaBadge>
										</div>
									</RecentCard>
								);
							})}
							{analytics.recentEvents.length === 0 && <EmptyState>No recent signals yet.</EmptyState>}
						</RecentList>
					</Panel>
				</AnalyticsGrid>

				<PaginationWrapperTop>
					<NumberPaginationBox
						page={page}
						count={total}
						per={PER_PAGE}
						onChange={(nextPage) => setPage(nextPage)}
					/>
				</PaginationWrapperTop>

				<MyBookingTableContainerAdmin style={{ overflowX: "auto" }}>
					{loading ? (
						<div style={{ display: "flex", justifyContent: "center", padding: 36 }}>
							<CircularProgress size={30} />
						</div>
					) : (
						<Table aria-label="landing search tracking table" style={{ minWidth: 1560 }}>
							<TableHead>
								<TableRow>
									<CommonTableHeadCell align="center">Captured at</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Event</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Source</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Status</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Search context</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Result</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Map context</CommonTableHeadCell>
									<CommonTableHeadCell align="center">Page</CommonTableHeadCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{docs.map((record) => {
									const eventMeta = getEventMeta(record.eventType);
									const statusMeta =
										record.eventType === "custom_near_me"
											? getStatusMeta(record.nearMeStatus)
											: getStatusMeta(record.searchStatus);
									const sourceMeta = getSourceMeta(record.searchSource);
									const modeMeta = getModeMeta(record.searchMode);

									return (
										<TableRow key={record.id}>
											<TableCell align="center">{moment(record.createdAt).format("HH:mm DD-MM-YYYY")}</TableCell>
											<TableCell align="center">
												<MetaBadge $color={eventMeta.color} $accent={eventMeta.accent}>
													{eventMeta.label}
												</MetaBadge>
											</TableCell>
											<TableCell align="center">
												<MetaBadge $color={sourceMeta.color} $accent={sourceMeta.accent}>
													{sourceMeta.label}
												</MetaBadge>
											</TableCell>
											<TableCell align="center">
												<MetaBadge $color={statusMeta.color} $accent={statusMeta.accent}>
													{statusMeta.label}
												</MetaBadge>
											</TableCell>
											<TableCell align="left">
												<DetailText>
													<span>
														<strong>Term:</strong> {record.searchTerm || "--"}
													</span>
													<span>
														<strong>Trigger:</strong> {record.searchTrigger || "--"}
													</span>
													<span>
														<strong>Session:</strong> {record.sessionId || "--"}
													</span>
												</DetailText>
											</TableCell>
											<TableCell align="left">
												<DetailText>
													<span>
														<strong>Label:</strong> {record.searchResultLabel || "--"}
													</span>
													<span>
														<strong>Place ID:</strong> {record.searchPlaceId || "--"}
													</span>
													<span>
														<strong>Error:</strong> {record.errorMessage || "--"}
													</span>
												</DetailText>
											</TableCell>
											<TableCell align="left">
												<DetailText>
													<span>
														<strong>Mode:</strong> {modeMeta.label}
													</span>
													<span>
														<strong>Radius:</strong> {record.searchRadiusM || 0} m
													</span>
													<span>
														<strong>Coordinates:</strong> {formatCoordinates(record)}
													</span>
												</DetailText>
											</TableCell>
											<TableCell align="left">
												<DetailText>
													<span>
														<strong>Path:</strong> {record.pagePath || "--"}
													</span>
													<span>
														<strong>URL:</strong> {record.pageLocation || "--"}
													</span>
												</DetailText>
											</TableCell>
										</TableRow>
									);
								})}
								{docs.length === 0 && (
									<TableRow>
										<TableCell align="center" colSpan={8}>
											No landing search events have been tracked yet.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					)}
				</MyBookingTableContainerAdmin>

				<PaginationWrapper>
					<NumberPaginationBox
						page={page}
						count={total}
						per={PER_PAGE}
						onChange={(nextPage) => setPage(nextPage)}
					/>
				</PaginationWrapper>
			</BookingContentWrap>
		</MyBookingWrapper>
	);
}
